import React, { useState, useEffect, useRef } from 'react';
import '../../styles/Home/Gallery.css';
import { getFirestore, doc, getDoc } from "firebase/firestore";
import { getApp } from "firebase/app";

const Gallery = () => {
  const [position, setPosition] = useState(1); // default to first card
  const [paused, setPaused] = useState(false);
  const [galleryImages, setGalleryImages] = useState([]);

  // Fallback static images
  const fallbackImages = [
    {
      id: 1,
      src: "/assets/hero1.jpg",
      name: "Goa",
      occupation: "Marketing Admin",
      testimonial: "Lorem ipsum dolor sit amet consectetur adipisicing elit. Iusto, suscipit?"
    },
    {
      id: 2,
      src: "/assets/hero2.jpg",
      name: "Kerala",
      occupation: "Cybersecurity Engineer",
      testimonial: "Lorem ipsum dolor sit amet consectetur adipisicing elit. Dolorem, dolore."
    },
    {
      id: 3,
      src: "/assets/hero3.jpg",
      name: "Rajasthan",
      occupation: "Software Engineer",
      testimonial: "Lorem ipsum dolor sit amet consectetur, adipisicing elit. Consequatur, temporibus?"
    },
    {
      id: 4,
      src: "/assets/hero4.jpg",
      name: "Himachal Pradesh",
      occupation: "UI/UX Designer",
      testimonial: "Lorem ipsum dolor sit amet, consectetur adipisicing elit. Quod, beatae?"
    },
    {
      id: 5,
      src: "/assets/hero1.jpg",
      name: "Uttarakhand",
      occupation: "System Architect",
      testimonial: "Lorem ipsum dolor sit amet, consectetur adipisicing elit. Esse, tempore."
    }
  ];

  // Fetch images from Firestore
  useEffect(() => {
    const fetchGalleryImages = async () => {
      try {
        const db = getFirestore(getApp());
        const docRef = doc(db, "gallery", "allimages");
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const images = docSnap.data().images || [];
          // Use caption from DB if available, fallback to default
          const mapped = images.map((img, idx) => ({
            id: idx + 1,
            src: img.url,
            name: img.caption
              ? img.caption
              : `Gallery Image ${idx + 1}`,
            occupation: "",
            testimonial: ""
          }));
          setGalleryImages(mapped.length > 0 ? mapped : fallbackImages);
        } else {
          setGalleryImages(fallbackImages);
        }
      } catch (err) {
        setGalleryImages(fallbackImages);
      }
    };
    fetchGalleryImages();
  }, []);

  // Helper for CSS variable style
  const carouselStyle = {
    '--items': galleryImages.length,
    '--middle': Math.ceil(galleryImages.length / 2),
    '--position': position,
  };

  const handlePrev = () => {
    setPosition(position === 1 ? galleryImages.length : position - 1);
  };

  const handleNext = () => {
    setPosition(position === galleryImages.length ? 1 : position + 1);
  };

  // Automatic scroll logic
  useEffect(() => {
    if (paused) return;
    const interval = setInterval(() => {
      setPosition(prev =>
        prev < galleryImages.length ? prev + 1 : 1
      );
    }, 3000);
    return () => clearInterval(interval);
  }, [paused, galleryImages.length]);

  // Pause on hover/focus
  const carouselRef = useRef(null);
  useEffect(() => {
    const node = carouselRef.current;
    if (!node) return;
    const pause = () => setPaused(true);
    const resume = () => setPaused(false);
    node.addEventListener('mouseenter', pause);
    node.addEventListener('mouseleave', resume);
    node.addEventListener('focusin', pause);
    node.addEventListener('focusout', resume);
    return () => {
      node.removeEventListener('mouseenter', pause);
      node.removeEventListener('mouseleave', resume);
      node.removeEventListener('focusin', pause);
      node.removeEventListener('focusout', resume);
    };
  }, []);

  // Swipe gesture state
  const touchStartX = useRef(null);
  const touchEndX = useRef(null);

  // Handle touch start
  const onTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  // Handle touch move
  const onTouchMove = (e) => {
    touchEndX.current = e.touches[0].clientX;
  };

  // Handle touch end
  const onTouchEnd = () => {
    if (touchStartX.current === null || touchEndX.current === null) return;
    const diff = touchStartX.current - touchEndX.current;
    if (Math.abs(diff) > 40) { // threshold
      if (diff > 0) {
        handleNext();
      } else {
        handlePrev();
      }
    }
    touchStartX.current = null;
    touchEndX.current = null;
  };

  return (
    <section className="gallery-carousel-container">
      <h2 className="gallery-title">Our Gallery</h2>
      <p className="gallery-subtitle">Discover Our Trips from around the world</p>
      <div
        className="carousel-slider-controls"
        ref={carouselRef}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        <button
          className="carousel-btn carousel-btn-left"
          onClick={handlePrev}
          aria-label="Previous"
        >
          &#8592;
        </button>
        <main id="carousel" style={carouselStyle}>
          {galleryImages.map((image, idx) => (
            <div
              className={`item${position === idx + 1 ? ' active' : ''}`}
              key={image.id}
              style={{ '--offset': idx + 1 }}
            >
              <img src={image.src} alt={image.name} />
              <span className="gallery-location-chip">{image.name}</span>
            </div>
          ))}
        </main>
        <button
          className="carousel-btn carousel-btn-right"
          onClick={handleNext}
          aria-label="Next"
        >
          &#8594;
        </button>
      </div>
      <div className="gallery-radio-group">
        {galleryImages.map((_, idx) => (
          <input
            key={idx}
            type="radio"
            name="position"
            checked={position === idx + 1}
            onChange={() => setPosition(idx + 1)}
            className="gallery-radio"
          />
        ))}
      </div>
    </section>
  );
};

export default Gallery;
